import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api } from '../api/client'
import type { AccessEvento, AccessPersona, AccessStatoAccount } from '../api/types'
import { Card, ErrorBanner, Spinner, formatDateTime } from '../components/common'
import { DataTable } from '../components/DataTable'
import { BadgeGravita, etichettaTipoIdentita, colonneStatoAccount } from '../components/access/comuni'
import { colonneEventi } from '../components/access/Eventi'
import { useStatoAccess } from '../hooks/useAccess'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/**
 * CENSO ACCESS: one person — the account names that make it (and how they
 * were joined: by SID, UPN, domain name, or by the name alone), its
 * privileges, the systems it reached in the last 30 days with how many
 * accesses off hours and failed, its detections, its timeline. From here a
 * wrong correlation is split and two identities that are one are merged.
 */
export default function AccessPersonPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const stato = useStatoAccess()
  const dati = useAsync(() => api.accessPerson(id), [id])
  const [eventi, setEventi] = useState<AccessEvento[] | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [unisci, setUnisci] = useState('')
  // The changes to type, on-call and note wait for «Salva», as in every form.
  const [bozza, setBozza] = useState<{ kind: AccessPersona['kind']; on_call: boolean; note: string } | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [salvato, setSalvato] = useState(false)
  const persone = useAsync(() => (stato.scrive ? api.accessPeople(false) : Promise.resolve({ people: [] as AccessPersona[] })), [stato.scrive])

  useEffect(() => {
    api.accessEvents({ identity_id: id, since: new Date(Date.now() - 30 * 86400_000).toISOString() }, null, 500)
      .then((r) => setEventi(r.events)).catch(setErrore)
  }, [id])

  const p = dati.data
  if (dati.loading && !p) return <Spinner />
  if (!p) return <ErrorBanner error={dati.error} />

  const campi = bozza ?? { kind: p.kind, on_call: p.on_call, note: p.note ?? '' }
  const modificato = bozza !== null
    && (bozza.kind !== p.kind || bozza.on_call !== p.on_call || bozza.note !== (p.note ?? ''))
  function cambia(parte: Partial<typeof campi>) {
    setSalvato(false)
    setBozza({ ...campi, ...parte })
  }
  async function salva() {
    if (!bozza) return
    setErrore(null)
    setSalvando(true)
    try {
      await api.accessPersonUpdate(id, { kind: bozza.kind, on_call: bozza.on_call, note: bozza.note.trim() || null })
      await dati.reload()
      setBozza(null)
      setSalvato(true)
    } catch (e) {
      setErrore(e)
    } finally {
      setSalvando(false)
    }
  }
  async function separa(chiave: string) {
    if (!window.confirm(t('Separare «{k}» in una nuova persona? Gli eventi di quell’account la seguiranno.', { k: chiave }))) return
    try {
      const nuova = await api.accessPersonSplit(id, chiave)
      navigate(`/access/people/${nuova.id}`)
    } catch (e) {
      setErrore(e)
    }
  }
  async function unisciCon() {
    if (!unisci || !window.confirm(t('Unire questa persona a quella scelta? Account, privilegi e rilevamenti passano a lei.'))) return
    try {
      const b = await api.accessPersonMerge(id, unisci)
      navigate(`/access/people/${b.id}`)
    } catch (e) {
      setErrore(e)
    }
  }
  const origine = (o: string) => ({ event: t('dagli eventi'), name: t('per nome'), entra: t('da Entra ID'), inventory: t('dall’inventario'), manual: t('a mano') } as Record<string, string>)[o] ?? o

  return (
    <>
      <header className="page__header">
        <div>
          <p className="muted" style={{ margin: 0 }}><Link to="/access/people">{t('Persone')}</Link></p>
          <h1>{p.display_name}</h1>
          <p className="page__subtitle">
            {etichettaTipoIdentita(p.kind)}{p.privileged && <> · <span className="badge badge--warn">{t('privilegiata')}</span></>}
            {p.on_call && <> · <span className="badge badge--info">{t('reperibile')}</span></>}
            {p.last_seen && <> · {t('ultimo evento {q}', { q: formatDateTime(p.last_seen) })}</>}
          </p>
          {p.note && <p className="muted" style={{ margin: '4px 0 0', whiteSpace: 'pre-wrap' }}>{p.note}</p>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link className="chip" to={`/access/events?persona=${p.id}&periodo=90d`}>{t('Tutti i suoi eventi')}</Link>
        </div>
      </header>
      <ErrorBanner error={errore} />

      <Card title={t('Account')}>
        <DataTable rows={(p.keys ?? []).map((k) => {
          const c = ((p.attributes?.accounts ?? {}) as Record<string, Record<string, unknown>>)[k] ?? {}
          return {
            k,
            account_enabled: typeof c.enabled === 'boolean' ? c.enabled : null,
            account_locked: typeof c.locked === 'boolean' ? c.locked : null,
            password_never_expires: typeof c.password_never_expires === 'boolean' ? c.password_never_expires : null,
            password_last_set: typeof c.password_last_set === 'string' ? c.password_last_set : null,
            last_logon: typeof c.last_logon === 'string' ? c.last_logon : null,
          }
        })} rowKey={(r) => r.k} paginazione={false} strumenti={false} columns={[
          { key: 'k', header: t('Account'), mono: true, value: (r) => r.k.split(':').slice(1).join(':') },
          { key: 'tipo', header: t('Tipo di chiave'), value: (r) => ({ sid: t('SID di Windows'), entra: t('oggetto di Entra ID'), upn: t('UPN'), sam: t('account di dominio'), local: t('account locale'), name: t('nome') } as Record<string, string>)[r.k.split(':')[0]] ?? r.k },
          { key: 'origine', header: t('Collegato'), value: (r) => origine(p.key_origins[r.k] ?? '') },
          ...colonneStatoAccount<{ k: string } & AccessStatoAccount>(),
          ...(stato.scrive && (p.keys ?? []).length > 1 ? [{ key: 'az', header: '', sort: false, filter: false as const, searchable: false, render: (r: { k: string }) => (
            <button type="button" className="linklike" onClick={() => void separa(r.k)}>{t('separa')}</button>
          ) }] : []),
        ]} />
        {stato.scrive && (
          <div className="form-grid" style={{ marginTop: 12 }}>
            <label className="field span-2"><span className="field__label">{t('Tipo')}</span>
              <select value={campi.kind} onChange={(e) => cambia({ kind: e.target.value as AccessPersona['kind'] })}>
                {['person', 'service', 'shared', 'builtin', 'unknown'].map((k) => <option key={k} value={k}>{etichettaTipoIdentita(k)}</option>)}
              </select></label>
            <label className="checkbox span-2"><input type="checkbox" checked={campi.on_call} onChange={(e) => cambia({ on_call: e.target.checked })} />{' '}{t('Reperibile: i suoi accessi fuori orario non sono un rilevamento')}</label>
            <label className="field span-2"><span className="field__label">{t('Unisci a un’altra persona')}</span>
              <span style={{ display: 'flex', gap: 6 }}>
                <select value={unisci} onChange={(e) => setUnisci(e.target.value)}>
                  <option value="">{t('scegli…')}</option>
                  {(persone.data?.people ?? []).filter((x) => x.id !== p.id).map((x) => <option key={x.id} value={x.id}>{x.display_name}</option>)}
                </select>
                <button type="button" className="secondary" onClick={() => void unisciCon()} disabled={!unisci}>{t('Unisci')}</button>
              </span></label>
            <label className="field span-6"><span className="field__label">{t('Nota')}</span>
              <textarea rows={2} maxLength={4000} value={campi.note} placeholder={t('per esempio: account del servizio SQL Server, gestito dal team database')}
                onChange={(e) => cambia({ note: e.target.value })} /></label>
            <div className="span-6" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={() => void salva()} disabled={!modificato || salvando}>{salvando ? t('Salvo…') : t('Salva')}</button>
              {modificato && <button type="button" className="secondary" onClick={() => setBozza(null)} disabled={salvando}>{t('Annulla le modifiche')}</button>}
              {modificato ? <span className="muted">{t('Modifiche non salvate.')}</span>
                : salvato && <span className="badge badge--ok">{t('Salvato.')}</span>}
            </div>
          </div>
        )}
      </Card>

      <Card title={t('Gruppi ({n})', { n: p.groups?.length ?? 0 })}>
        {(p.groups?.length ?? 0) === 0 ? (
          <p className="muted" style={{ margin: 0 }}>
            {t('Nessun gruppo descritto. I gruppi delle utenze di dominio li manda l’agente del domain controller (aggiornato): per tutte le utenze con gli utenti non privilegiati accesi, altrimenti per le privilegiate.')}
          </p>
        ) : (
          <DataTable rows={p.groups ?? []} rowKey={(g) => `${g.account}|${g.group}|${g.indirect}`} conteggio={{ nome: t('gruppi') }} columns={[
            { key: 'group', header: t('Gruppo'), value: (g) => g.group,
              render: (g) => <span className={g.privileged ? 'persona-gruppi__priv' : undefined}>{g.group}{g.privileged && <> <span className="badge badge--warn">{t('privilegiato')}</span></>}</span> },
            { key: 'indirect', header: t('Come'), filter: 'select', value: (g) => g.indirect ? t('tramite un altro gruppo') : t('diretto') },
            { key: 'privileged', header: t('Privilegiato'), filter: 'select', value: (g) => g.privileged ? t('sì') : t('no') },
            { key: 'account', header: t('Utenza'), mono: true, value: (g) => g.account },
          ]} />
        )}
      </Card>

      <Card title={t('Sistemi raggiunti (30 giorni)')}>
        <DataTable rows={p.systems} rowKey={(s) => `${s.ci_id}-${s.host}`} conteggio={{ nome: t('sistemi') }} initialSort={{ key: 'events', dir: 'desc' }} columns={[
          { key: 'ci', header: t('Sistema'), value: (s) => s.ci_name ?? s.host ?? '', render: (s) => <>{s.ci_name ?? s.host ?? '—'}{s.ci_class && <><br /><small className="muted">{s.ci_class}</small></>}</> },
          { key: 'events', header: t('Eventi'), type: 'number', value: (s) => s.events },
          { key: 'off_hours', header: t('Fuori orario'), type: 'number', value: (s) => s.off_hours },
          { key: 'failures', header: t('Falliti'), type: 'number', value: (s) => s.failures },
          { key: 'last', header: t('Ultimo'), type: 'date', value: (s) => s.last ?? '', render: (s) => formatDateTime(s.last) },
        ]} />
      </Card>

      {p.privileges_list.length > 0 && (
        <Card title={t('Privilegi')}>
          <DataTable rows={p.privileges_list} rowKey={(r) => r.id} conteggio={{ nome: t('privilegi') }} columns={[
            { key: 'scope_name', header: t('Su che cosa'), value: (r) => r.scope_name },
            { key: 'account', header: t('Con l’account'), mono: true, value: (r) => r.account },
            { key: 'active', header: t('Stato'), filter: 'select', value: (r) => (r.active ? (r.eligible ? t('idoneo') : t('attivo')) : t('cessato')) },
            { key: 'first_seen', header: t('Da'), type: 'date', value: (r) => r.first_seen ?? '', render: (r) => formatDateTime(r.first_seen) },
          ]} />
        </Card>
      )}

      {p.detections.length > 0 && (
        <Card title={t('Rilevamenti')}>
          <DataTable rows={p.detections} rowKey={(d) => d.id} conteggio={{ nome: t('rilevamenti') }} columns={[
            { key: 'code', header: t('ID'), mono: true, value: (d) => d.code },
            { key: 'severity', header: t('Gravità'), filter: 'select', value: (d) => d.severity, render: (d) => <BadgeGravita g={d.severity} /> },
            { key: 'title', header: t('Cosa'), value: (d) => d.title },
            { key: 'last_at', header: t('Ultimo'), type: 'date', value: (d) => d.last_at, render: (d) => formatDateTime(d.last_at) },
            { key: 'status', header: t('Stato'), filter: 'select', value: (d) => d.status },
          ]} />
        </Card>
      )}

      <Card title={t('Cronologia (30 giorni)')}>
        {eventi === null ? <Spinner /> : (
          <DataTable rows={eventi} columns={colonneEventi(false)} rowKey={(e) => String(e.seq)} conteggio={{ nome: t('eventi') }}
                     rowClassName={(e) => (e.outcome === 'failure' ? 'accriga--ko' : e.off_hours && e.privileged ? 'accriga--fuori' : undefined)} />
        )}
      </Card>
    </>
  )
}
